import { useMemo, useEffect, useState, useRef, useCallback, type MutableRefObject } from "react";
import { twMerge } from "tailwind-merge";
import { z, ZodTypeAny } from "zod";
import { AnimatePresence, motion } from "motion/react";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import {
  type QuizQuestionSet as QuizQuestionSetType,
  makeQuestionSetFormFields,
  buildFormSchemaFromFields,
  filterFieldsByQuestionLogic,
} from "@core/features/quiz";
import { QuizFormField } from "../QuizFormField";
import { StepActions } from "@themes/pollinator/ui/StepActions";

export type Props = {
  questionSet: QuizQuestionSetType;
  onSubmit: (data: z.infer<ZodTypeAny>) => Promise<void>;
  onBack: () => void;
  defaultValues: Record<string, unknown>;
  filterQuestionSet?: (
    question: QuizQuestionSetType["questions"][number],
    index: number
  ) => boolean;
  isSubmitting: boolean;
  stepIndex: number;
  totalSteps: number;
  buttonLabel: string;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** Callback when validation state changes */
  onStateChange?: (state: { isValid: boolean; isSubmitting: boolean }) => void;
};

export function QuizQuestionSet({
  questionSet,
  buttonLabel,
  onSubmit,
  onBack,
  defaultValues,
  filterQuestionSet = () => true,
  stepIndex,
  totalSteps,
  isSubmitting: isSubmittingProp = false,
  showActionButtons = true,
  submitRef,
  onStateChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [animationDirection, setAnimationDirection] = useState(1);

  const allFields = useMemo(
    () =>
      makeQuestionSetFormFields(
        questionSet.questions.filter(filterQuestionSet)
      ),
    [questionSet, filterQuestionSet]
  );

  const schema = useMemo(
    () => buildFormSchemaFromFields(allFields),
    [allFields]
  );

  const { form, handleSubmit } = useForm({
    defaultValues,
    schema,
    onSubmit,
  });

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues);
    }
  }, [defaultValues, stepIndex, form]);

  const values = form.watch();

  const visibleFields = useMemo(() => {
    return filterFieldsByQuestionLogic(allFields, values);
  }, [allFields, values]);

  const { isValid, isSubmitting } = form.formState;

  const submitAndUpdateAnimationDirection = useCallback(async () => {
    await handleSubmit();
    setAnimationDirection(1);
  }, [handleSubmit]);

  // Expose submit handler to parent via ref
  useEffect(() => {
    if (submitRef) {
      submitRef.current = submitAndUpdateAnimationDirection;
    }
  }, [submitRef, submitAndUpdateAnimationDirection]);

  // Track if we recently submitted to avoid state update race conditions
  const recentlySubmittedRef = useRef(false);

  // Use ref to hold callback to avoid infinite loops when callback changes
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  // Report validation state changes to parent
  // Skip reporting briefly after submission to avoid race conditions with index changes
  useEffect(() => {
    if (recentlySubmittedRef.current) {
      return;
    }
    onStateChangeRef.current?.({
      isValid,
      isSubmitting: isSubmitting || isSubmittingProp,
    });
  }, [isValid, isSubmitting, isSubmittingProp, stepIndex]);

  // Mark as recently submitted when submitting, clear after a delay
  useEffect(() => {
    if (isSubmitting || isSubmittingProp) {
      recentlySubmittedRef.current = true;
    } else if (recentlySubmittedRef.current) {
      // Clear after animation/transition completes
      const timer = setTimeout(() => {
        recentlySubmittedRef.current = false;
        // Report final state after transition settles
        onStateChangeRef.current?.({
          isValid,
          isSubmitting: false,
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isSubmitting, isSubmittingProp, isValid]);

  const handleBack = () => {
    setAnimationDirection(-1);
    onBack();
  };

  return (
    <div className="flex-1 flex flex-col justify-between max-h-screen overflow-y-auto">
      <AnimatePresence mode="wait" custom={animationDirection}>
        <motion.div
          key={stepIndex}
          ref={containerRef}
          initial={{ x: animationDirection * 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          style={{ width: "100%" }}
          exit={{ x: animationDirection * -100, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className="flex-1 flex flex-col justify-center"
        >
          <Form
            key={stepIndex}
            form={form}
            className={twMerge("w-full bg-brand-50 text-brand-800")}
            innerClassName={twMerge("w-full mx-auto gap-12")}
            errorContainerClassName="px-8 max-w-xl mx-auto mb-8"
          >
            {visibleFields.map((field) => {
              // Omit labelVariant from spread if it's "overline" (not supported by TypeVariant)
              const { labelVariant, ...fieldProps } = field;
              const safeVariant =
                labelVariant === "overline" ? "caption" : labelVariant;
              return (
                <QuizFormField
                  key={field.name}
                  {...fieldProps}
                  labelVariant={safeVariant}
                  className="gap-4"
                />
              );
            })}
          </Form>
        </motion.div>
      </AnimatePresence>
      {showActionButtons && (
        <StepActions
          onBack={handleBack}
          onPrimaryClick={submitAndUpdateAnimationDirection}
          primaryLabel={buttonLabel}
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          isPrimaryDisabled={!isValid || isSubmitting || isSubmittingProp}
        />
      )}
    </div>
  );
}

export default QuizQuestionSet;


